import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  Button,
  Tag,
  Input,
  Icon,
  Tooltip,
  Alert,
  Modal,
  message,
  Checkbox,
  Spin
} from 'antd'
import './CharacterMappingTable.scss'
import CharacterManagerModal from './CharacterManagerModal'
import WardrobeSelectorModal from '../Wardrobe/WardrobeSelectorModal'
import WardrobeManagerModal from '../Wardrobe/WardrobeManagerModal'

const CharacterMappingTable = ({
  placeholderCharacters,
  characters,
  isFetchingCharacters,
  onAddCharacter,
  changedCharacters,
  onCharacterChange,
  characterMap,
  isAllCharactersChanged,
  shotList = [],
  onAddNewCharacter,
  onDeleteCharacter,
  onClearShotMedia,
  fetchCharacters,
  isDraftMode = false,
  wardrobe = {},
  outfitMap = {},
  outfitsReference = {},
  onWardrobeSave,
  formData = {},
  setFormData = () => {},
  videoId
}) => {
  // Normalize malformed props
  const normalizedCharacterMap =
    characterMap === 'null' ? {} : characterMap || {}

  const normalizedCharacters = useMemo(() => {
    // Collect hook characters
    const hookChars =
      characters && Array.isArray(characters) && characters.length > 0
        ? characters
        : []

    // Collect formData characters
    const formChars =
      formData.characterList &&
      Array.isArray(formData.characterList) &&
      formData.characterList.length > 0
        ? formData.characterList
        : []

    // Return hook chars if available, otherwise fallback to form chars
    if (hookChars.length > 0) return hookChars
    return formChars
  }, [characters, formData.characterList])

  const normalizedPlaceholderCharacters =
    placeholderCharacters === 'null' ? [] : placeholderCharacters || []

  const [isManageModalVisible, setIsManageModalVisible] = useState(false)
  const [isWardrobeManagerVisible, setIsWardrobeManagerVisible] = useState(
    false
  )
  const [isWardrobeSelectorVisible, setIsWardrobeSelectorVisible] = useState(
    false
  )
  const [wardrobeSelectorAction, setWardrobeSelectorAction] = useState(null)
  const [selectedWardrobeChar, setSelectedWardrobeChar] = useState({
    name: null,
    key: null
  })
  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchText, setSearchText] = useState('')
  const dropdownRefs = useRef({})
  const triggerRefs = useRef({})
  const [dropdownStyleMap, setDropdownStyleMap] = useState({})

  // Add character modal state
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [newCharacterName, setNewCharacterName] = useState('')
  const [isAddingCharacter, setIsAddingCharacter] = useState(false)

  // Confirmation Modal State
  const [confirmModalData, setConfirmModalData] = useState({
    visible: false,
    affectedScenes: [],
    originalChar: '',
    selectedChar: null,
    onConfirm: null
  })
  const [shouldClearFinalVideo, setShouldClearFinalVideo] = useState(false)

  // --- CHARACTER MAP UTILITIES ---
  /**
   * Check if a characterMap value is in the new format (object with name and id)
   * New format: { name: "Carl", character_id: "KXZ7X3" }
   * Legacy format: "Carl" (string)
   */
  const isNewMapFormat = mapValue => {
    return mapValue && typeof mapValue === 'object' && mapValue.name
  }

  /**
   * Get character name from characterMap (supports both formats)
   */
  const getCharacterNameFromMap = charKey => {
    const mapValue = normalizedCharacterMap[charKey]
    if (!mapValue) return ''

    // New format: { name: "Carl", character_id: "KXZ7X3" }
    if (isNewMapFormat(mapValue)) {
      return mapValue.name
    }

    // Legacy format: "Carl" (string)
    return mapValue
  }

  /**
   * Get character_id from characterMap (supports both formats)
   * Returns null for legacy format
   */
  const getCharacterIdFromMap = charKey => {
    const mapValue = normalizedCharacterMap[charKey]
    if (!mapValue) return null

    // New format: { name: "Carl", character_id: "KXZ7X3" }
    if (isNewMapFormat(mapValue)) {
      return mapValue.character_id
    }

    // Legacy format: no character_id available
    return null
  }

  /**
   * Find a character object by name and/or character_id from characterMap
   * Prioritizes character_id match over name match
   */
  const findCharacterByMapping = charKey => {
    const mapValue = normalizedCharacterMap[charKey]
    if (!mapValue) return null

    // New format: try to find by character_id first
    if (isNewMapFormat(mapValue) && mapValue.character_id) {
      const byId = normalizedCharacters.find(
        c => c.character_id === mapValue.character_id
      )
      if (byId) return byId
    }

    // Fallback: find by name (for both legacy and new format)
    const name = isNewMapFormat(mapValue) ? mapValue.name : mapValue

    // If multiple characters have the same name, we can't reliably identify which one
    const byName = normalizedCharacters.filter(c => c.name === name)

    if (byName.length === 1) {
      return byName[0]
    }

    // Multiple matches or no match
    return null
  }

  // --- Utils ---
  const isCharacterMapped = character =>
    changedCharacters.some(char => char.original.includes(character))

  const getMappedCharacterName = character => {
    const mapping = changedCharacters.find(c => c.original.includes(character))
    if (mapping) return mapping.current.replace(/^_{2}(.+)_{2}$/, '$1')
    return character.replace(/^_{2}(.+)_{2}$/, '$1')
  }

  const getMappedCharacterId = character => {
    const mapping = changedCharacters.find(c => c.original.includes(character))
    return mapping?.tag || null
  }

  const handleCharChange = (originalChar, selectedChar) => {
    // Close dropdown immediately to prevent overlap with modal
    setOpenDropdown(null)
    setSearchText('')

    const cleanChar = originalChar.replace(/^_{2}(.+)_{2}$/, '$1')

    // Group affected shots by scene
    const affectedScenesMap = {}

    shotList.forEach((scene, sceneIdx) => {
      if (scene.shots) {
        scene.shots.forEach((shot, shotIdx) => {
          // Check if shot has generated media
          if (shot.imageSrc || shot.vidSrc) {
            // Check if the original character is used in prompts
            const fieldsToCheck = [shot.initial_image_prompt, shot.video_prompt]

            // Also check dialogue if needed, but prompts are main drivers for media
            if (shot.dialogue) {
              Object.keys(shot.dialogue).forEach(dialogueChar => {
                // Additional checks can be added here
              })
            }

            const isCharInPrompt = fieldsToCheck.some(
              field =>
                field &&
                typeof field === 'string' &&
                field.includes(originalChar)
            )

            if (isCharInPrompt) {
              const sceneKey = sceneIdx + 1
              if (!affectedScenesMap[sceneKey]) {
                affectedScenesMap[sceneKey] = {
                  title: `Scene ${sceneKey}`,
                  shots: []
                }
              }

              affectedScenesMap[sceneKey].shots.push({
                id: shot.id,
                shotIdx: shotIdx + 1,
                title: shot.title || `Shot ${shotIdx + 1}`
              })
            }
          }
        })
      }
    })

    const affectedScenes = Object.values(affectedScenesMap)

    const doChange = () => {
      const firstChar = isCharacterMapped(originalChar) ? null : undefined
      onCharacterChange(
        cleanChar,
        selectedChar.name,
        firstChar,
        selectedChar.character_id
      )
    }

    if (affectedScenes.length > 0) {
      setShouldClearFinalVideo(false) // Reset default
      setConfirmModalData({
        visible: true,
        affectedScenes,
        originalChar,
        selectedChar,
        onConfirm: () => {
          const allShotIds = []
          affectedScenes.forEach(scene => {
            scene.shots.forEach(s => allShotIds.push(s.id))
          })
        }
      })
    } else {
      doChange()
    }
  }

  const handleConfirmChange = () => {
    const { affectedScenes, onConfirm } = confirmModalData
    if (onConfirm) {
      const allShotIds = []
      affectedScenes.forEach(scene => {
        scene.shots.forEach(s => allShotIds.push(s.id))
      })
      onClearShotMedia(allShotIds, shouldClearFinalVideo)

      // Execute the change
      const { originalChar, selectedChar } = confirmModalData
      const cleanChar = originalChar.replace(/^_{2}(.+)_{2}$/, '$1')
      const firstChar = isCharacterMapped(originalChar) ? null : undefined
      onCharacterChange(
        cleanChar,
        selectedChar.name,
        firstChar,
        selectedChar.character_id
      )
    }
    setConfirmModalData({ ...confirmModalData, visible: false })
  }

  const getCharacterName = character => {
    return getCharacterNameFromMap(character)
  }

  const getWardrobeInfo = charKey => {
    const charOutfits = wardrobe[charKey] || []
    if (charOutfits.length === 0) return { status: 'none', count: 0, total: 0 }

    const charReferences = outfitsReference[charKey] || {}
    const refCount = Object.keys(charReferences).filter(
      key => charOutfits.includes(key) && charReferences[key]
    ).length

    let status = 'pending'
    if (refCount === charOutfits.length) status = 'ready'
    else if (refCount > 0) status = 'incomplete'

    return { status, count: refCount, total: charOutfits.length }
  }

  const isAllWardrobeReady = useMemo(() => {
    return normalizedPlaceholderCharacters.every(char => {
      const { status, total } = getWardrobeInfo(char)
      if (total === 0) return true // No wardrobe required
      return status === 'ready'
    })
  }, [normalizedPlaceholderCharacters, wardrobe, outfitsReference])

  const mappedCharactersCount = useMemo(() => {
    return normalizedPlaceholderCharacters.filter(char =>
      changedCharacters.some(changed => changed.original.includes(char))
    ).length
  }, [normalizedPlaceholderCharacters, changedCharacters])

  const totalCharactersCount = normalizedPlaceholderCharacters.length
  const unmappedCharactersCount = Math.max(
    0,
    totalCharactersCount - mappedCharactersCount
  )
  const hasIncompleteCharacterMapping = isDraftMode
    ? unmappedCharactersCount > 0
    : !isAllCharactersChanged
  const shouldShowActionRequiredAlert =
    hasIncompleteCharacterMapping || !isAllWardrobeReady

  // --- Check if character is used in any shot (by character_id or placeholder) ---
  const isCharacterInUse = useMemo(() => {
    const usageMap = {}

    normalizedPlaceholderCharacters.forEach(charKey => {
      let isUsed = false
      const usageByScene = {}

      // Get the mapped character_id for this placeholder
      const mappedCharId = getMappedCharacterId(charKey)

      // Check each scene and shot for character usage
      shotList.forEach((scene, sceneIdx) => {
        scene.shots &&
          scene.shots.forEach((shot, shotIdx) => {
            const fieldsToCheck = [
              shot.initial_image_prompt,
              shot.video_prompt,
              shot.scenery,
              shot.voiceover_text
            ]

            // Also check dialogue keys
            if (shot.dialogue) {
              Object.keys(shot.dialogue).forEach(dialogueChar => {
                fieldsToCheck.push(dialogueChar)
              })
            }

            // Search for character placeholder OR mapped character_id in any field
            const isFoundInShot = fieldsToCheck.some(field => {
              if (!field || typeof field !== 'string') return false
              return (
                field.includes(charKey) ||
                (mappedCharId && field.includes(mappedCharId))
              )
            })

            if (isFoundInShot) {
              isUsed = true
              const sceneNum = sceneIdx + 1
              if (!usageByScene[sceneNum]) {
                usageByScene[sceneNum] = []
              }
              if (!usageByScene[sceneNum].includes(shotIdx + 1)) {
                usageByScene[sceneNum].push(shotIdx + 1)
              }
            }
          })
      })

      // Format usage for display: "Scene 1 (Shots 1, 5), Scene 2 (Shots 1, 4)"
      const formattedUsage = Object.entries(usageByScene).map(
        ([sceneNum, shots]) => {
          const shotLabel = shots.length === 1 ? 'Shot' : 'Shots'
          return `Scene ${sceneNum} (${shotLabel} ${shots.join(', ')})`
        }
      )

      usageMap[charKey] = {
        isUsed,
        usageByScene,
        formattedUsage,
        totalScenes: Object.keys(usageByScene).length
      }
    })

    return usageMap
  }, [normalizedPlaceholderCharacters, shotList, changedCharacters])

  // --- Get next available character ID ---
  const getNextCharacterId = () => {
    const existingIds = Object.keys(normalizedCharacterMap).map(key => {
      const match = key.match(/__character(\d+)__/)
      return match ? parseInt(match[1], 10) : 0
    })
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0
    return `__character${maxId + 1}__`
  }

  // --- Add new character handler ---
  const handleAddNewCharacterSubmit = () => {
    if (!newCharacterName.trim()) {
      message.warning('Please enter a character name')
      return
    }

    setIsAddingCharacter(true)
    const newCharId = getNextCharacterId()

    if (onAddNewCharacter) {
      onAddNewCharacter(newCharId, newCharacterName.trim())
      message.success(`Character "${newCharacterName}" added successfully`)
    }

    setNewCharacterName('')
    setIsAddModalVisible(false)
    setIsAddingCharacter(false)

    // Refresh character list from API
    if (fetchCharacters) {
      fetchCharacters(true)
    }
  }

  // --- Delete character handler ---
  const handleDeleteCharacterClick = charKey => {
    const charName = getCharacterName(charKey) || charKey

    Modal.confirm({
      title: 'Delete Character',
      content: `Are you sure you want to delete "${charName}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => {
        if (onDeleteCharacter) {
          onDeleteCharacter(charKey)
          message.success(`Character "${charName}" deleted`)
        }
      }
    })
  }

  // --- Dropdown positioning ---
  const updateDropdownPosition = () => {
    if (openDropdown && triggerRefs.current[openDropdown]) {
      const rect = triggerRefs.current[openDropdown].getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const MIN_WIDTH = 350

      setDropdownStyleMap(prev => {
        // Calculate width - use trigger width but at least 350px
        const width = Math.max(rect.width, MIN_WIDTH)

        // Calculate left - try to align with trigger, but push left if it overflows right
        let left = rect.left
        if (left + width > viewportWidth - 20) {
          // 20px padding from right edge
          left = Math.max(20, viewportWidth - width - 20)
        }

        const newStyle = {
          position: 'fixed',
          top: rect.bottom + 4,
          left: left,
          width: width,
          zIndex: 2000
        }

        return {
          ...prev,
          [openDropdown]: newStyle
        }
      })
    }
  }

  useEffect(() => {
    if (openDropdown) {
      // Check available space and scroll if needed
      if (triggerRefs.current[openDropdown]) {
        const rect = triggerRefs.current[openDropdown].getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom
        // Default max height from CSS is 400px, but usually it's around 300px for reasonable lists.
        // We use 320px as a safe threshold to ensure visibility.
        const MIN_REQUIRED_SPACE = 320

        if (spaceBelow < MIN_REQUIRED_SPACE) {
          const scrollAmount = MIN_REQUIRED_SPACE - spaceBelow + 20 // 20px padding
          window.scrollBy({ top: scrollAmount, behavior: 'smooth' })
        }
      }

      updateDropdownPosition()

      const handleScroll = () => {
        requestAnimationFrame(updateDropdownPosition)
      }

      // Capture phase to catch all scrolls (including nested containers)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)

      return () => {
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [openDropdown])

  useEffect(() => {
    const handleOutside = e => {
      // Check if click is inside dropdown content or trigger
      if (openDropdown && dropdownRefs.current[openDropdown]) {
        if (
          !dropdownRefs.current[openDropdown].contains(e.target) &&
          !triggerRefs.current[openDropdown].contains(e.target)
        ) {
          setOpenDropdown(null)
        }
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [openDropdown])

  // --- Check if character is selected (using character_id with legacy fallback) ---
  const isCharSelected = (char, originalChar) => {
    const mapping = changedCharacters.find(
      c => c.original === originalChar || c.original.includes(originalChar)
    )
    if (!mapping) return false

    // NEW FORMAT: Use character_id from mapping.tag (most reliable)
    if (mapping.tag && char.character_id === mapping.tag) {
      return true
    }

    // LEGACY FORMAT FALLBACK: Use name matching only if:
    // 1. No character_id in mapping (legacy data)
    // 2. There's only ONE character with this name (no ambiguity)
    if (!mapping.tag) {
      const cleanCurrent = mapping.current.replace(/^_{2}(.+)_{2}$/, '$1')
      const charsWithSameName = normalizedCharacters.filter(
        c => c.name === cleanCurrent
      )

      // Only use name matching if there's exactly one character with this name
      if (charsWithSameName.length === 1 && char.name === cleanCurrent) {
        return true
      }
    }

    return false
  }

  // --- Filter characters for dropdown (deduplicate by character_id) ---
  const getFilteredCharacters = originalChar => {
    const query = searchText.toLowerCase()

    // 1. Filter by search query
    const filtered = normalizedCharacters.filter(c => {
      const attrs = c.attributes || {}
      return (
        (c.name || '').toLowerCase().includes(query) ||
        (c.gender || '').toLowerCase().includes(query) ||
        (attrs.original_prompt || '').toLowerCase().includes(query) ||
        (attrs.character_apparel || '').toLowerCase().includes(query)
      )
    })

    // 2. Deduplicate by character_id (in case there are duplicates in the data)
    const deduped = filtered.reduce((acc, char) => {
      if (!acc.find(c => c.character_id === char.character_id)) {
        acc.push(char)
      }
      return acc
    }, [])

    // 3. Sort so selected stays at top
    return deduped.sort((a, b) => {
      const aSelected = isCharSelected(a, originalChar)
      const bSelected = isCharSelected(b, originalChar)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })
  }

  // --- Check if there are duplicate names in the character list ---
  const getCharacterNameCount = useMemo(() => {
    const nameCount = {}
    normalizedCharacters.forEach(char => {
      nameCount[char.name] = (nameCount[char.name] || 0) + 1
    })
    return nameCount
  }, [normalizedCharacters])

  // --- Dropdown option render (using character_id as key) ---
  const renderCharacterOption = (char, originalChar) => {
    const attrs = char.attributes || {}
    const prompt = attrs.original_prompt || ''
    const pic = attrs.profile_pic || ''
    const apparels = (attrs.character_apparel || '').replace(/[\[\]']/g, '')
    const genderLabel =
      typeof char.gender === 'string' ? char.gender.trim() : ''
    const isSelected = isCharSelected(char, originalChar)

    // Check if there are other characters with the same name
    const hasDuplicateName = getCharacterNameCount[char.name] > 1

    return (
      <div
        key={char.character_id}
        className={`custom-character-option ${isSelected ? 'selected' : ''}`}
        onClick={() => handleCharChange(originalChar, char)}
      >
        <img
          src={pic}
          alt={char.name}
          className="custom-character-avatar"
          onError={e => (e.target.style.display = 'none')}
        />
        <div className="custom-character-info">
          <div className="custom-character-name">
            {char.name} {genderLabel ? <Tag>{genderLabel.toUpperCase()}</Tag> : null}
            {hasDuplicateName && (
              <Tag
                color="orange"
                style={{ marginLeft: '4px', fontSize: '11px' }}
              >
                ID: {char.character_id}
              </Tag>
            )}
          </div>
          {prompt && (
            <div className="custom-character-prompt">
              {prompt.length > 50 ? prompt.slice(0, 50) + '...' : prompt}
            </div>
          )}
          {apparels && (
            <div className="custom-character-apparel">{apparels}</div>
          )}
        </div>
        {isSelected && (
          <Icon
            type="check"
            className="selected-icon"
            style={{ color: '#1d4ed8', fontSize: '16px' }}
          />
        )}
      </div>
    )
  }

  // --- Dropdown ---
  const renderCustomSelect = text => {
    const isOpen = openDropdown === text
    const filtered = getFilteredCharacters(text)
    const mappedCharId = getMappedCharacterId(text)

    return (
      <div
        className="custom-select-wrapper"
        ref={el => {
          triggerRefs.current[text] = el
        }}
      >
        <div
          className={`custom-select-trigger ${isOpen ? 'open' : ''}`}
          onClick={() => setOpenDropdown(isOpen ? null : text)}
        >
          <span className="custom-select-value">
            {getMappedCharacterName(text)}
            {mappedCharId && (
              <Tag
                color="blue"
                style={{
                  marginLeft: '8px',
                  fontSize: '11px',
                  fontWeight: 'normal'
                }}
              >
                ID: {mappedCharId}
              </Tag>
            )}
          </span>
          <Icon type="down" className="custom-select-arrow" />
        </div>

        {isOpen && (
          <div
            className="custom-select-dropdown"
            style={dropdownStyleMap[text] || {}}
            ref={el => {
              dropdownRefs.current[text] = el
            }}
          >
            <div className="custom-select-search">
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={e => setSearchText(e.target?.value)}
                autoFocus
                prefix={<Icon type="search" style={{ color: '#ccc' }} />}
              />
            </div>
            <div className="custom-select-options">
              {isFetchingCharacters ? (
                <div className="custom-select-loading">
                  <Spin size="small" />
                  <span>Loading characters...</span>
                </div>
              ) : filtered.length ? (
                filtered.map(c => renderCharacterOption(c, text))
              ) : (
                <div className="custom-select-empty">No characters found</div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // --- Render delete button or tooltip ---
  const renderDeleteAction = charKey => {
    const usage = isCharacterInUse[charKey] || {
      isUsed: false,
      formattedUsage: [],
      totalScenes: 0
    }

    if (usage.isUsed) {
      const displayedScenes = usage.formattedUsage.slice(0, 2)
      const remainingScenes = usage.totalScenes - 2

      return (
        <Tooltip
          title={
            <span>
              Cannot delete: Used in {displayedScenes.join(', ')}
              {remainingScenes > 0 &&
                ` and ${remainingScenes} more scene${
                  remainingScenes > 1 ? 's' : ''
                }`}
            </span>
          }
          placement="left"
        >
          <span className="delete-btn disabled">
            <Icon type="delete" />
          </span>
        </Tooltip>
      )
    }

    return (
      <Tooltip title="Delete character" placement="left">
        <span
          className="delete-btn"
          onClick={() => handleDeleteCharacterClick(charKey)}
        >
          <Icon type="delete" />
        </span>
      </Tooltip>
    )
  }

  // --- Render ---
  return (
    <div className="character-mapping-container">
      {/* Header */}
      <div
        className="character-mapping-header"
        style={{ justifyContent: isDraftMode ? 'flex-end' : 'space-between' }}
      >
        {!isDraftMode && (
          <div className="header-content">
            <h3 className="header-title">Character Mapping</h3>
            <p className="header-description">
              Assign valid characters to the placeholders extracted from your
              script.
            </p>
          </div>
        )}
        <div className="header-actions">
          <Button
            onClick={() => setIsManageModalVisible(true)}
            className="manage-characters-btn"
            icon="setting"
          >
            Manage Characters
          </Button>
          <Button
            onClick={() => {
              setIsWardrobeManagerVisible(true)
            }}
            className="manage-characters-btn"
            icon="skin"
          >
            Manage Wardrobe
          </Button>
        </div>
      </div>

      {shouldShowActionRequiredAlert && (
        <Alert
          message="Action Required"
          description={
            hasIncompleteCharacterMapping && !isAllWardrobeReady
              ? `Please complete both steps before continuing: map all placeholder characters and select wardrobe references for required outfits${
                  isDraftMode && unmappedCharactersCount > 0
                    ? ` (${unmappedCharactersCount} character${
                        unmappedCharactersCount === 1 ? '' : 's'
                      } still unmapped).`
                    : '.'
                }`
              : hasIncompleteCharacterMapping
              ? `Please map all placeholder characters to generated characters before continuing${
                  isDraftMode && unmappedCharactersCount > 0
                    ? ` (${unmappedCharactersCount} remaining).`
                    : '.'
                }`
              : 'Please select wardrobe references for all required outfits before continuing.'
          }
          type="warning"
          showIcon
          style={{ marginBottom: '24px', borderRadius: '8px' }}
        />
      )}

      {/* Custom Table */}
      <div className="custom-table">
        <div className="custom-table-header">
          <div className="header-cell">Story Placeholder</div>
          <div className="header-cell">Assigned Character</div>
          <div className="header-cell outfit-header">Outfit Selection</div>
          <div className="header-cell status-header">Status</div>
          <div className="header-cell actions-header">Actions</div>
        </div>

        {normalizedPlaceholderCharacters.map((char, i) => {
          const mapped = isCharacterMapped(char)
          const associatedName = getCharacterName(char)

          return (
            <div key={i} className="custom-table-row">
              <div className="cell original">
                <div className="placeholder-name">
                  <Tooltip title="Original name from script">
                    {associatedName || char}
                  </Tooltip>
                </div>
                {char && <div className="script-name-tag">{char}</div>}
              </div>
              <div className="cell mapto">{renderCustomSelect(char)}</div>

              <div className="cell outfit">
                <div className="outfit-selection-cell">
                  {getWardrobeInfo(char).total > 0 ? (
                    <>
                      <Button
                        type="link"
                        className={`wardrobe-action-btn ${
                          getWardrobeInfo(char).status
                        }`}
                        onClick={() => {
                          setSelectedWardrobeChar({
                            name: associatedName || char,
                            key: char
                          })
                          setWardrobeSelectorAction(null)
                          setIsWardrobeSelectorVisible(true)
                        }}
                        icon="skin"
                      >
                        Wardrobe
                      </Button>
                      <span className="outfit-count-label">
                        Outfits ({getWardrobeInfo(char).count}/
                        {getWardrobeInfo(char).total}) selected
                      </span>
                    </>
                  ) : (
                    <>
                      <Button
                        type="link"
                        className="wardrobe-action-btn"
                        onClick={() => {
                          setSelectedWardrobeChar({
                            name: associatedName || char,
                            key: char
                          })
                          setWardrobeSelectorAction('addCustomOutfit')
                          setIsWardrobeSelectorVisible(true)
                        }}
                        icon="plus"
                      >
                        Add Outfit
                      </Button>
                      <span
                        className="no-wardrobe-label"
                        style={{ marginLeft: 8 }}
                      >
                        No Wardrobe Req.
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="cell status">
                <div className="dual-status-container">
                  <div
                    className={`status-indicator ${
                      mapped ? 'mapped' : 'pending'
                    }`}
                  >
                    <Icon
                      type={mapped ? 'check-circle' : 'clock-circle'}
                      theme={mapped ? 'filled' : 'outlined'}
                      className="status-icon"
                    />
                    <span className="label">
                      {mapped ? 'Mapped Character' : 'Pending mapping'}
                    </span>
                  </div>
                  <div
                    className={`status-indicator wardrobe-${
                      getWardrobeInfo(char).status
                    }`}
                  >
                    {(() => {
                      const { status } = getWardrobeInfo(char)
                      let iconType = 'clock-circle'
                      let iconTheme = 'outlined'

                      if (status === 'ready') {
                        iconType = 'check-circle'
                        iconTheme = 'filled'
                      } else if (status === 'incomplete') {
                        iconType = 'exclamation-circle'
                        iconTheme = 'filled'
                      } else if (status === 'none') {
                        iconType = 'minus-circle'
                      }

                      return (
                        <Icon
                          type={iconType}
                          theme={iconTheme}
                          className="status-icon"
                        />
                      )
                    })()}
                    <span className="label">
                      {(() => {
                        const { status } = getWardrobeInfo(char)
                        if (status === 'ready') return 'Mapped All Outfits'
                        if (status === 'incomplete')
                          return 'Wardrobe Incomplete'
                        if (status === 'pending') return 'Wardrobe Pending'
                        return 'No Wardrobe Req.'
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="cell actions">{renderDeleteAction(char)}</div>
            </div>
          )
        })}

        {!isDraftMode && (
          <div
            className="add-character-row"
            onClick={() => setIsAddModalVisible(true)}
          >
            <div className="add-character-trigger">
              <Icon type="plus-circle" theme="filled" />
              <span>Add New Character</span>
            </div>
          </div>
        )}
      </div>

      {/* Add Character Modal */}
      <Modal
        maskClosable={false}
        visible={isAddModalVisible}
        title="Add New Character"
        onCancel={() => {
          setIsAddModalVisible(false)
          setNewCharacterName('')
        }}
        onOk={handleAddNewCharacterSubmit}
        okText="Add Character"
        confirmLoading={isAddingCharacter}
        centered
        className="add-character-modal"
      >
        <div className="add-character-form">
          <div className="form-field">
            <label>Character Name</label>
            <Input
              placeholder="Enter character name (e.g., 'Romeo', 'Narrator')"
              value={newCharacterName}
              onChange={e => setNewCharacterName(e.target.value)}
              onPressEnter={handleAddNewCharacterSubmit}
              autoFocus
              size="large"
            />
          </div>
          <p className="form-hint">
            <Icon type="info-circle" /> A unique character ID will be
            automatically generated.
          </p>
        </div>
      </Modal>

      <CharacterManagerModal
        visible={isManageModalVisible}
        onClose={() => setIsManageModalVisible(false)}
        characters={normalizedCharacters}
        onAddCharacter={onAddCharacter}
        isFetchingCharacters={isFetchingCharacters}
        fetchCharacters={fetchCharacters}
        videoId={videoId}
      />

      <WardrobeManagerModal
        visible={isWardrobeManagerVisible}
        onClose={() => setIsWardrobeManagerVisible(false)}
        formData={formData}
        setFormData={setFormData}
        videoId={videoId}
      />

      <WardrobeSelectorModal
        visible={isWardrobeSelectorVisible}
        onClose={() => {
          setIsWardrobeSelectorVisible(false)
          setWardrobeSelectorAction(null)
        }}
        initialAction={wardrobeSelectorAction}
        characterName={selectedWardrobeChar.name}
        characterKey={selectedWardrobeChar.key}
        wardrobe={wardrobe}
        outfitMap={outfitMap}
        outfitsReference={outfitsReference}
        onSave={onWardrobeSave}
        formData={formData}
        setFormData={setFormData}
        videoId={videoId}
      />

      {/* Confirmation Modal */}
      <Modal
        maskClosable={false}
        visible={confirmModalData.visible}
        title="Confirm Character Change"
        onCancel={() =>
          setConfirmModalData({ ...confirmModalData, visible: false })
        }
        onOk={handleConfirmChange}
        okText="Confirm & Change Character"
        cancelText="Cancel"
        centered
        width={700}
        className="character-change-confirmation-modal"
      >
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <Icon
            type="exclamation-circle"
            theme="filled"
            style={{ color: '#faad14', fontSize: '22px', marginTop: '4px' }}
          />
          <div className="confirmation-content">
            <div className="confirmation-header-text">
              The character{' '}
              <b>"{getMappedCharacterName(confirmModalData.originalChar)}"</b>{' '}
              is currently used in the shots listed below.
              <br />
              <br />
              Since images and videos have already been generated using this
              character, changing it to{' '}
              <b>"{confirmModalData.selectedChar?.name}"</b> will{' '}
              <span style={{ fontWeight: 600, color: '#761414ff' }}>
                remove the generated media
              </span>{' '}
              <strong>(image and video)</strong> for these shots to maintain
              consistency.
              <br />
              <br />
              The previous media will still be available in the history.{' '}
            </div>

            <h3 style={{ marginTop: '20px' }}>Shots Affected</h3>
            <div className="affected-shots-list">
              {confirmModalData.affectedScenes.map((scene, idx) => (
                <div key={idx} className="scene-group">
                  <div className="scene-title">{scene.title}</div>
                  <ul>
                    {scene.shots.map(shot => (
                      <li key={shot.id}>
                        <span className="shot-label">Shot {shot.shotIdx}:</span>
                        <span className="shot-title">{shot.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '16px' }}>
              <Checkbox
                checked={shouldClearFinalVideo}
                onChange={e => setShouldClearFinalVideo(e.target.checked)}
              >
                Select this option to also remove the <b>Final Video</b> (if
                generated)
              </Checkbox>
            </div>

            <div className="confirmation-note" style={{ marginTop: '16px' }}>
              <span style={{ fontWeight: 600 }}>Note:</span> The media will be
              auto-regenerated when you proceed to the Image Generation and
              Video Generation steps.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default CharacterMappingTable
